import React, { useState, useRef } from "react";
import { useDispatch } from "react-redux";

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from "@mui/material/Divider";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from "@mui/material/IconButton";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Table from '../helpers/Table'
import DeletePopup from "../helpers/DeletePopup";

import { addDriver, editDriver, deleteDriver, clearQueryResult } from "../../actions/valueActions";

import { formatString, convertBoolDiffValue } from "./valueFunctions";
import { getIconFromDriverName } from "./iconFunctions";
import RevenueValueDriverIRForm from "./RevenueValueDriverIRForm";
import { makePlural } from "../helpers/stringFormat";
import ModalElement from "../Modal";
import AddRevenueData from "./AddRevenueData";
import { toolTipUserIcon } from "./iconFunctions";
import { getAllOutcomes } from "./valueFunctions";

const RevenueValueDriverIR = ({ id, valueModel, revenueTypes, driver, outcome }) => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [open, setOpen] = React.useState(false);
    const [selectedRevenue, setSelectedRevenue] = useState(Object.keys(revenueTypes)[0])
    const [deleteIRDriverPopupOpen, setDeleteIRDriverPopupOpen] = useState(false);
    const [deleteIRDriverId, setDeleteIRDriverId] = useState(null);
    const [editIRDriverId, setEditIRDriverId] = useState(null);
    const [addRevenueModalOpen, setAddRevenueModalOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleRevenueChange = (event) => {
        setSelectedRevenue(event.target.value)
        if (editIRDriverId) {
            setEditIRDriverId(null)
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }

    const openDeleteIRDriver = (id) => {
        setDeleteIRDriverId(id)
        setDeleteIRDriverPopupOpen(true)
    }

    const editIRDriver = (id) => {
        setEditIRDriverId(id)
        let irDriver = valueModel.revenue_value_driver.incremental_revenue.find(irDriver => irDriver.id === id)
        setSelectedRevenue(irDriver.revenue)
    }

    const handleResetIR = () => {
        setEditIRDriverId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    const toggleAddRevenueModalOpen = () => {
        setAddRevenueModalOpen(!addRevenueModalOpen)
        dispatch(clearQueryResult())
    }

    function renderAddRevenueModal() {
        return (
            <ModalElement
                modalOpen={addRevenueModalOpen}
                modalWidth={1000}
                handleClose={toggleAddRevenueModalOpen}
                header="Add Revenue Type"
                instruction="Please specify what you would like to add and provide a wish that will bring some context to your request"
                content={
                    <AddRevenueData
                        id={id}
                        industry={valueModel?.industry}
                        type='incremental'
                        revenueTypes={revenueTypes}
                        handleClose={toggleAddRevenueModalOpen}
                    />
                }
            />

        )
    }

    const onSubmitIR = async (values, driver, { setSubmitting, setErrors }) => {
        values.id = editIRDriverId
        if (outcome) {
            values.outcome = outcome
        }
        values.driver = driver
        values.revenue = selectedRevenue
        let success = false

        if (editIRDriverId) {
            const result = dispatch(editDriver('incremental', id, values, { setSubmitting, setErrors }))
            success = result?.success;
            if (success) {
                setEditIRDriverId(null)
            }
        }
        else {
            const result = dispatch(addDriver('incremental', id, values, { setSubmitting, setErrors }))
            success = result?.success;
        }

        if (formikRef.current && success) {
            formikRef.current.resetForm();
        }
    }

    const irDriverColumns = [
        { id: 'outcome', label: 'Outcome', minWidth: 170, action: false },
        { id: 'driver_name', label: 'Name', minWidth: 170, action: false },
        { id: 'revenue', label: 'Type', format: (value) => formatString(value), minWidth: 150, action: false },
        { id: 'multiple_value', label: 'Opportunity', minWidth: 100, action: false },
        { id: 'probability', label: 'Probability of sales', format: (value) => value + ' %', minWidth: 100, action: false },
        // { id: 'units_needed', label: 'Units needed', format: (value) => value + ' units', minWidth: 100, action: false },
        { id: 'positive_driver', label: 'Differentiation value', minWidth: 100, format: (value) => convertBoolDiffValue(Boolean(value)), action: false },
        { id: 'actions', label: 'Actions', minWidth: 100, action: false },
    ];

    const createIRDriverData = (id, outcome, driver_name, revenue, multiple, multiple_value, probability, units_needed, positive_driver, actions) => {
        multiple_value = `${multiple_value} ${makePlural(multiple)}${positive_driver ? '' : ' lost'}`
        return { id, outcome, driver_name, revenue, multiple_value, probability, units_needed, positive_driver, actions };
    }

    const irDriverActions = (id) => {
        return (
            <React.Fragment>
                <IconButton size="small" onClick={() => editIRDriver(id)}>
                    <EditIcon />
                </IconButton>
                <IconButton size="small" onClick={() => openDeleteIRDriver(id)}>
                    <DeleteIcon />
                </IconButton>
            </React.Fragment>
        )
    }

    const irDriverRows = valueModel?.revenue_value_driver.incremental_revenue.filter(driver => !outcome || driver.outcome === outcome).map((driver) => {
        return (
            createIRDriverData(driver.id, driver.outcome, driver.driver_name, driver.revenue, driver.multiple, driver.multiple_value, driver.probability, driver.units_needed, driver.positive_driver, irDriverActions(driver.id))
        )
    })

    const irDriverTable = () => {
        return (
            <React.Fragment>
                <Divider />
                <Table
                    columns={irDriverColumns}
                    rows={irDriverRows}
                    selectedId={editIRDriverId}
                />
            </React.Fragment>
        )
    }

    const irDriverFormActions = () => {
        return (
            <React.Fragment>
                {editIRDriverId ? null
                    : <Button variant="contained" startIcon={<Box component={getIconFromDriverName(driver)} />} size="small" type="submit">Add driver</Button>
                }
                {editIRDriverId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit">Save</Button>
                        <Button variant="contained" startIcon={<CancelIcon />} size="small" onClick={handleResetIR}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    return (
        <Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Stack gap={1} direction="row" sx={{ ml: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                        Driver type:
                    </Typography>
                    <Typography variant="caption">
                        Profit Opportunity
                    </Typography>
                </Stack>
                <Box sx={{ width: '30%' }} />
                <Button startIcon={<AutoFixHighIcon />} onClick={toggleAddRevenueModalOpen} sx={{ textTransform: 'none' }}>Add Revenue Type</Button>
            </Box>

            <Stack direction="column" gap={1} sx={{ ml: 2, mt: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                    1. Select Revenue Type
                </Typography>
                <Box>
                    <Select
                        open={open}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        value={selectedRevenue}
                        onChange={handleRevenueChange}
                        size='small'
                    >
                        {Object.keys(revenueTypes).map((revenue) => (
                            <MenuItem key={revenue} value={revenue}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    {formatString(revenue)}
                                    {revenueTypes[revenue]?.user_created && toolTipUserIcon()}
                                </Box>

                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Stack>

            <RevenueValueDriverIRForm
                currency="€"
                outcome={outcome}
                outcomeList={getAllOutcomes(valueModel?.driver_analysis)}
                product={valueModel?.product}
                revenueType={revenueTypes[selectedRevenue]}
                editIRDriverId={editIRDriverId}
                editIRDriver={valueModel?.revenue_value_driver.incremental_revenue.find(irDriver => irDriver.id === editIRDriverId)}
                formActions={irDriverFormActions}
                onSubmit={(values, { setSubmitting, setErrors }) => onSubmitIR(values, driver, { setSubmitting, setErrors })}
                ref={formikRef}
            />
            {irDriverTable()}
            <DeletePopup open={deleteIRDriverPopupOpen} onClose={() => setDeleteIRDriverPopupOpen(false)} onDelete={() => dispatch(deleteDriver('incremental', id, deleteIRDriverId))} />
            {renderAddRevenueModal()}
        </Box>
    )

}

export default RevenueValueDriverIR